import * as React from 'react';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { AriaRoles } from 'owa-accessibility';
import { IconButton } from '@fluentui/react/lib/Button';
import { CharmControl, CharmControlColorPattern, CharmControlColorProps } from 'owa-charmcontrol';
import { CharmInfo, getDefaultCharms } from 'owa-calendar-charms-store';
import { FocusZone, FocusZoneTabbableElements } from '@fluentui/react/lib/FocusZone';

import classNames from 'classnames';
import styles from './CharmPicker.scss';

export interface CharmPickerProps {
    selectedIcon?: CharmInfo;
    onSelectIcon: (icon: CharmInfo) => void;
    color: ColorScheme;

    /**
     * Use !important on styles because component default styles take priority
     */
    containerClassName?: string;

    /**
     * Use !important on styles because component default styles take priority
     */
    buttonClassName?: string;

    /**
     * Use !important on styles because component default styles take priority
     */
    iconClassName?: string;
    disabled?: boolean;
    trapFocus?: boolean;
    ariaLabelledBy?: string;
}

export default function CharmDefaultSet(props: CharmPickerProps) {
    /**
     * Make the div for an icon. Add extra style for the slected icon
     */
    const createIconDiv = (charm: CharmInfo) => {
        let primaryColor = props.color.accessibleText.msColorWhiteBackground;
        let colorProps: CharmControlColorProps = {
            colorPattern: CharmControlColorPattern.Normal,
            calPrimary: primaryColor,
            calDark: props.color.calDark,
            calDarkAlt: props.color.calDarkAlt,
            textColor: props.color.accessibleText.calPrimaryBackground,
        };
        let isSelected = props.selectedIcon != null && charm.IconId == props.selectedIcon.IconId;
        const onButtonClick = () => onSelectIcon(charm);
        return (
            <div key={charm.IconId}>
                <IconButton
                    id={charm.Name + 'charm'}
                    key={'ButtonKey-' + charm.IconId}
                    className={classNames(
                        styles.charmDefaultSeticonButton,
                        isSelected && styles.selected,
                        props.buttonClassName
                    )}
                    ariaLabel={charm.LocalizedName}
                    title={charm.LocalizedName}
                    aria-selected={isSelected}
                    role={AriaRoles.option}
                    onClick={onButtonClick}
                    disabled={props.disabled}>
                    <CharmControl
                        iconId={charm.IconId}
                        className={classNames(styles.charmIcon, props.iconClassName)}
                        colorProps={colorProps}
                    />
                </IconButton>
            </div>
        );
    };
    /**
     * Create a list of JSX elements for every icon in the default set
     */
    const createAllIconDivs = () => {
        let retElemets: JSX.Element[] = [];
        let includeUnsetCharm = true;
        let defaultCharms = getDefaultCharms(includeUnsetCharm);
        defaultCharms.forEach(element => {
            let charm = element as CharmInfo;
            if (charm) {
                retElemets.push(createIconDiv(charm));
            }
        });
        return retElemets;
    };
    /**
     * Select an icon
     */
    const onSelectIcon = (icon: CharmInfo) => {
        let { onSelectIcon } = props;
        if (onSelectIcon) {
            onSelectIcon(icon);
        }
    };
    return (
        <FocusZone
            key={'charmCallout'}
            role={AriaRoles.listbox}
            aria-labelledby={props.ariaLabelledBy}
            className={
                props.containerClassName != null
                    ? props.containerClassName
                    : classNames(styles.charmContainer)
            }
            isCircularNavigation={true}
            handleTabKey={
                props.trapFocus ? FocusZoneTabbableElements.all : FocusZoneTabbableElements.none
            }>
            {createAllIconDivs()}
        </FocusZone>
    );
}
